import { flushSync } from "react-dom";
import { showsMotion } from "@/lib/hooks/motion";

/**
 * A move between the site's places, run as a view transition: the old
 * page fades out, the new one rises in, the header holds still and the
 * nav's green indicator slides from one place to the next and snaps
 * into it (index.css, "Moving between places"). Where the browser has no
 * view transitions, its reader asked for less motion, or the page is
 * hidden, the move simply happens.
 */
export function withViewTransition(move: () => void): void {
    const start = (
        document as Document & {
            startViewTransition?: (update: () => void) => {
                ready?: Promise<void>;
                finished?: Promise<void>;
            };
        }
    ).startViewTransition;
    if (typeof start !== "function" || !showsMotion() || document.visibilityState === "hidden") {
        move();
        return;
    }
    // The new page has to be in the DOM when the browser takes its picture.
    const transition = start.call(document, () => flushSync(move));
    // The browser skips the animation when the page goes hidden or another
    // move starts, and rejects `ready` and `finished`. The move is still
    // made. A move that throws still rejects `updateCallbackDone`.
    transition?.ready?.catch(() => {});
    transition?.finished?.catch(() => {});
}

/** A plain left click: not opening a new tab, not a download. */
export function isPlainClick(event: {
    button: number;
    metaKey: boolean;
    altKey: boolean;
    ctrlKey: boolean;
    shiftKey: boolean;
    defaultPrevented: boolean;
}): boolean {
    return (
        !event.defaultPrevented &&
        event.button === 0 &&
        !event.metaKey &&
        !event.altKey &&
        !event.ctrlKey &&
        !event.shiftKey
    );
}
